import type { CalendarDayEntry, CalendarEvent } from '../../services/api';
import type { ATPPlan, ATPWeek } from '../../services/atpApi';
import { addDaysISO, weekTss, projectCtlAtDate, planStartISO, ATP_PRIORITY_A, daysBetweenISO } from './helpers';

export function loadNumber(value: unknown): number | null {
  if (value == null || value === '' || (typeof value === 'string' && !value.trim())) return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}
/** Never substitute planned load for missing actual load; deduplicate workout IDs. */
export function summarizeWeek(week: ATPWeek, days: CalendarDayEntry[], today: string, planEnd?: string) {
  const start = week.start_day.slice(0, 10);
  const end = planEnd && planEnd < addDaysISO(start, 6) ? planEnd : addDaysISO(start, 6);
  let planned = 0, actual = 0, plannedToDate = 0, missingPlanned = 0, missingActual = 0, sessions = 0, completed = 0;
  const seen = new Set<number>();
  for (const day of days) {
    const date = day.day.slice(0, 10);
    if (date < start || date > end) continue;
    for (const w of day.workouts ?? []) {
      if (seen.has(w.id)) continue;
      seen.add(w.id);
      if (Number(w.workout_type?.id) === 7) continue;
      sessions++;
      const p = loadNumber(w.tss_planned), a = loadNumber(w.tss_actual);
      if (p != null) { planned += p; if (date <= today) plannedToDate += p; }
      else missingPlanned++;
      if (date > today) continue;
      const done = (loadNumber(w.total_time) ?? 0) > 0 || (loadNumber(w.distance) ?? 0) > 0 || a != null;
      if (done) completed++;
      if (a != null) actual += a;
      else if (done) missingActual++;
    }
  }
  return { start, end, target: weekTss(week), planned, actual, plannedToDate, missingPlanned, missingActual, sessions, completed,
    future: start > today, past: end < today };
}

/** Same persisted ATP load model as the annual-plan projection, through the final week day. */
export function weekTargetFitness(plan: ATPPlan, weeks: ATPWeek[], end: string): number | null {
  const ordered = [...weeks].sort((a,b) => a.start_day.localeCompare(b.start_day));
  const baseline = loadNumber(plan.ctl_start);
  if (baseline == null || !ordered.length) return null;
  return projectCtlAtDate(baseline, ordered.map(weekTss), planStartISO(plan, ordered), addDaysISO(end, 1));
}

/** Nearest future A event in this ATP, independent of response order or selected calendar week. */
export function nearestMainGoal(events: CalendarEvent[], today: string, end: string) {
  const event = events.filter(e => e.atp_priority === ATP_PRIORITY_A && /^\d{4}-\d{2}-\d{2}/.test(e.event_date) && e.event_date.slice(0,10) >= today && e.event_date.slice(0,10) <= end)
    .sort((a,b) => a.event_date.localeCompare(b.event_date) || a.id-b.id)[0];
  return event ? { event, date: event.event_date.slice(0,10), days: daysBetweenISO(today, event.event_date.slice(0,10)) } : null;
}
